// RouteNotFound.tsx — Sprint R11-8 (ADR-128).
//
// Before this sprint the condo shell silently ignored any URL it didn't
// recognise: `/condo-app/nonsense` rendered the role's home page as though
// nothing had happened, and a page the user wasn't allowed to see did the
// same. Both are now honest Results.
//
// NOTE ON AUTHORIZATION: the 403 here is a UX guard, not a security boundary.
// It reuses the same role/feature gates that decide nav visibility so the
// chrome and the route agree. Real authorization is enforced server-side per
// endpoint (a forbidden page would render but its data calls would 403). Do
// not treat this component as protecting anything.
import React from 'react';
import { Button, Result } from 'antd';
import type { DashboardComponent } from './menuConfig';

interface RouteResultProps {
  /** Where "take me somewhere real" should go. */
  homeKey: DashboardComponent;
  onGoHome: (key: DashboardComponent) => void;
}

/** No such page — the URL doesn't match any entry in the page registry. */
export const RouteNotFound: React.FC<RouteResultProps & { slug: string }> = ({
  slug, homeKey, onGoHome,
}) => (
  <Result
    status="404"
    title="Page not found"
    subTitle={
      slug
        ? `There's no page at "${slug}" in this workspace.`
        : "That page doesn't exist in this workspace."
    }
    extra={
      <Button type="primary" onClick={() => onGoHome(homeKey)}>
        Back to my dashboard
      </Button>
    }
  />
);

/** The page exists, but this user's role/plan doesn't include it. */
export const RouteForbidden: React.FC<RouteResultProps & { label: string }> = ({
  label, homeKey, onGoHome,
}) => (
  <Result
    status="403"
    title="You don't have access to this page"
    subTitle={`${label} isn't available for your role in this workspace.`}
    extra={
      <Button type="primary" onClick={() => onGoHome(homeKey)}>
        Back to my dashboard
      </Button>
    }
  />
);
